import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Notiflix from 'notiflix';
import { SharedCardData } from './types';
import { useSharedCards } from './hooks/useSharedCards';
import CardSelector from './components/CardSelector';
import SharedCardDisplay from './components/SharedCardDisplay';
import {
  Container,
  Content,
  Header,
  Title,
  Subtitle,
  MainCardSection,
  CardSelectorSection,
  CardsDisplaySection,
  EmptyState,
  ErrorMessage,
  LogoSection,
} from './styles';

import { ExternalCardSharingInfo } from '../VirtualCard';

import cardSharingService from '../../services/cardSharingService';
import { useOnlineMode } from '../../hooks/useOnlineMode';

const SharedCards: React.FC = () => {
  const [sharedCardsData, setSharedCardsData] = useState<SharedCardData[]>([]);
  const [requestInfo, setRequestInfo] =
    useState<ExternalCardSharingInfo | null>(null);
  const { state, selectCard, setActiveTab } = useSharedCards(sharedCardsData);
  const history = useHistory();
  const { isOnline } = useOnlineMode();

  useEffect(() => {
    const walletData = localStorage.getItem(
      '@unimedblumenalexternalcardsharing',
    );

    const info = localStorage.getItem('@unimedblumenalexternalcardsharinginfo');

    if (!walletData || !info) {
      history.replace('/cartao-virtual-externo');
      return;
    }

    setSharedCardsData(JSON.parse(walletData));
    setRequestInfo(JSON.parse(info));
  }, [history]);

  useEffect(() => {
    if (!isOnline && !requestInfo) {
      history.replace('/cartao-virtual-externo');
      return;
    }

    const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;
    if (requestInfo && Date.now() - requestInfo.fetchedIn > THIRTY_DAYS_IN_MS) {
      refetchInfos();
    }
  }, [isOnline, requestInfo, history]);

  const refetchInfos = useCallback(async () => {
    if (!requestInfo) {
      Notiflix.Notify.failure('Falha ao atualizar os dados da carteirinha');
      return;
    }

    const response = await cardSharingService.getExternalAccessWallets(
      requestInfo.cpf,
      requestInfo.token,
    );

    if (!response) {
      Notiflix.Notify.failure(
        'Falha ao atualizar os dados do compartilhamento',
      );
      return;
    }

    localStorage.setItem(
      '@unimedblumenalexternalcardsharing',
      JSON.stringify(response),
    );

    const newRequestInfo = {
      fetchedIn: Date.now(),
      cpf: requestInfo.cpf,
      token: requestInfo.token,
    } as ExternalCardSharingInfo;

    localStorage.setItem(
      '@unimedblumenalexternalcardsharinginfo',
      JSON.stringify(newRequestInfo),
    );

    setSharedCardsData(response);
    setRequestInfo(newRequestInfo);
  }, [requestInfo]);

  if (state.availableCards.length === 0) {
    return (
      <Container>
        <Content>
          <LogoSection>
            <img
              src="/images/logo-unimed-horizontal.png"
              alt="Unimed Blumenau"
            />
          </LogoSection>
          <Header>
            <Title>Carteirinhas Compartilhadas</Title>
            <Subtitle>
              Visualize as carteirinhas que foram compartilhadas com você
            </Subtitle>
          </Header>
          <EmptyState>
            <h2>Nenhuma carteirinha compartilhada disponível</h2>
            <p>Não há carteirinhas válidas para exibir.</p>
          </EmptyState>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <LogoSection>
          <img src="/images/logo-unimed-horizontal.png" alt="Unimed Blumenau" />
        </LogoSection>
        <Header>
          <Title>Carteirinhas Compartilhadas</Title>
          <Subtitle>
            Visualize as carteirinhas que foram compartilhadas com você
          </Subtitle>
        </Header>

        <MainCardSection>
          <CardSelectorSection>
            <CardSelector
              cards={state.availableCards}
              selectedCard={state.selectedCard}
              onCardSelect={selectCard}
              disabled={state.loading}
            />
          </CardSelectorSection>

          <CardsDisplaySection>
            {state.selectedCard && requestInfo && (
              <SharedCardDisplay
                card={state.selectedCard}
                activeTab={state.activeTab}
                onTabChange={setActiveTab}
                onRequestRefetch={refetchInfos}
              />
            )}
          </CardsDisplaySection>

          {state.error && (
            <ErrorMessage>
              <p>Erro: {state.error}</p>
            </ErrorMessage>
          )}
        </MainCardSection>
      </Content>
    </Container>
  );
};

export default SharedCards;
